// Dynamic Section Renderer - Renders CMS sections on the public website
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Star, MapPin, Calendar, Users, Sparkles, Quote, Check, ChevronRight,
  Shield, Heart, Clock, Award, Globe, Target, Lightbulb, CheckCircle, Phone, Mail,
  Mountain, User, Leaf, Landmark
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { useSiteSettings } from './Layout';
import { normalizeImageUrl } from '../../../utils/normalizeImageUrl';
import { getOptimizedImageUrl } from '../../../utils/getOptimizedImageUrl';
import { getApiBase } from '../../../utils/apiBase';

// Compose normalize + optimize for public-facing card/grid imagery.
// (Hero backgrounds use raw normalize-only so LCP isn't affected.)
const optimized = (url) => getOptimizedImageUrl(normalizeImageUrl(url));
// Stats Bar Section
export const StatsBarSection = ({ section }) => {
  const stats = section.stats || section.data || section.config?.stats || [];
  return (
    <section className="relative -mt-12 z-10 pb-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500 rounded-3xl shadow-2xl shadow-orange-500/30 p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-white text-center">
            {stats.map((stat, idx) => (
              <div key={idx} className="space-y-1">
                <p className="text-3xl md:text-4xl font-bold">{stat.value}</p>
                <p className="text-white/80 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Destinations Grid Section
export const DestinationsGridSection = ({ section, destinations = [] }) => {
  const showCount = section.showCount || 4;
  const displayDestinations = destinations.slice(0, showCount);
  
  return (
    <section className="py-16 bg-slate-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-10">
          <div>
            {section.badge && (
              <span className="inline-block px-3 py-1 bg-rose-100 text-rose-600 rounded-full text-sm font-medium mb-2">
                {section.badge}
              </span>
            )}
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800">{section.title}</h2>
          </div>
          {section.linkText && (
            <a href="/destinations" target="_blank" rel="noopener noreferrer" className="text-rose-500 hover:text-rose-600 font-medium flex items-center gap-1">
              {section.linkText} <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {displayDestinations.map((dest) => (
            <a key={dest.id} href={`/destinations/${dest.slug || dest.id}`} target="_blank" rel="noopener noreferrer">
              <Card className="group overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all rounded-3xl">
                <div className="relative h-64">
                  <img src={optimized(dest.image)} alt={dest.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <h3 className="text-xl font-bold">{dest.name}</h3>
                    <p className="text-white/80 text-sm flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {dest.country}
                    </p>
                  </div>
                  {(dest.is_trending ?? dest.trending) && (
                    <span className="absolute top-4 right-4 px-2 py-1 bg-rose-500 text-white text-xs rounded-full">
                      Trending
                    </span>
                  )}
                </div>
              </Card>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// Themes/Travel Styles Grid Section
export const ThemesGridSection = ({ section, themes = [] }) => {
  // Icon mapping for themes
  const iconMap = {
    Mountain: Mountain,
    Users: Users,
    Heart: Heart,
    Sparkles: Sparkles,
    User: User,
    Leaf: Leaf,
    Landmark: Landmark
  };

  const defaultThemes = [
    { id: 'honeymoon', name: 'Honeymoon', icon: 'Heart', color: 'from-rose-400 to-pink-500', description: 'Romantic getaways' },
    { id: 'adventure', name: 'Adventure', icon: 'Mountain', color: 'from-emerald-400 to-teal-500', description: 'Thrilling experiences' },
    { id: 'family', name: 'Family', icon: 'Users', color: 'from-blue-400 to-indigo-500', description: 'Fun for everyone' },
    { id: 'solo', name: 'Solo', icon: 'User', color: 'from-amber-400 to-orange-500', description: 'Find yourself' },
    { id: 'friends', name: 'Friends', icon: 'Users', color: 'from-purple-400 to-pink-500', description: 'Create memories' },
    { id: 'wellness', name: 'Wellness', icon: 'Leaf', color: 'from-cyan-400 to-blue-500', description: 'Rejuvenate' },
    { id: 'cultural', name: 'Cultural', icon: 'Landmark', color: 'from-amber-500 to-red-500', description: 'Explore heritage' },
    { id: 'wildlife', name: 'Wildlife', icon: 'Mountain', color: 'from-green-400 to-emerald-500', description: 'Safari & nature' }
  ];
  
  const displayThemes = section.cards?.length > 0 ? section.cards : defaultThemes;
  const showCount = section.showCount || 8;
  
  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          {section.badge && (
            <span className="inline-block bg-orange-100 text-orange-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">
              {section.badge}
            </span>
          )}
          <h2 className="text-4xl font-bold text-slate-800">{section.title || 'How Do You Like to Travel?'}</h2>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {displayThemes.slice(0, showCount).map((theme, index) => {
            const IconComponent = iconMap[theme.icon] || Sparkles;
            return (
              <a
                key={theme.id || index}
                href={`/packages?theme=${theme.id || theme.name.toLowerCase()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group p-6 bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 text-center hover:-translate-y-2"
              >
                <div className={`w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center transition-colors ${
                  index % 4 === 0 ? 'bg-rose-100 group-hover:bg-rose-500' :
                  index % 4 === 1 ? 'bg-orange-100 group-hover:bg-orange-500' :
                  index % 4 === 2 ? 'bg-amber-100 group-hover:bg-amber-500' :
                  'bg-teal-100 group-hover:bg-teal-500'
                }`}>
                  <IconComponent className={`w-6 h-6 transition-colors ${
                    index % 4 === 0 ? 'text-rose-500 group-hover:text-white' :
                    index % 4 === 1 ? 'text-orange-500 group-hover:text-white' :
                    index % 4 === 2 ? 'text-amber-500 group-hover:text-white' :
                    'text-teal-500 group-hover:text-white'
                  }`} />
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{theme.name}</h3>
                <p className="text-sm text-slate-500">{theme.description}</p>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

import { getPackageUrl } from '../../../utils/getPackageUrl';
// Packages Grid Section
export const PackagesGridSection = ({ section, packages = [] }) => {
  const showCount = section.showCount || 3;
  const displayPackages = packages.slice(0, showCount);
  
  return (
    <section className="py-16 bg-slate-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-10">
          <div>
            {section.badge && (
              <span className="inline-block px-3 py-1 bg-rose-100 text-rose-600 rounded-full text-sm font-medium mb-2">
                {section.badge}
              </span>
            )}
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800">{section.title}</h2>
          </div>
          {section.linkText && (
            <a href="/packages" target="_blank" rel="noopener noreferrer" className="text-rose-500 hover:text-rose-600 font-medium flex items-center gap-1">
              {section.linkText} <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>
        
        <div className="grid md:grid-cols-3 gap-6">
          {displayPackages.map((pkg) => (
            <a key={pkg.id} href={getPackageUrl(pkg)} target="_blank" rel="noopener noreferrer">
              <Card className="group overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all rounded-3xl">
                <div className="relative h-56">
                  <img src={optimized(pkg.image)} alt={pkg.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-white/90 text-slate-800 rounded-full text-sm font-semibold">
                      {pkg.duration} Days
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <h3 className="text-xl font-bold">{pkg.title}</h3>
                    <p className="text-white/80 text-sm flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {typeof pkg.destination === 'object' ? pkg.destination.name : pkg.destination}
                    </p>
                  </div>
                </div>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-slate-400 line-through text-sm">₹{(pkg.original_price || pkg.originalPrice || 0).toLocaleString('en-IN')}</span>
                      <span className="text-2xl font-bold text-slate-800 ml-2">₹{(pkg.price || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex items-center gap-1 text-amber-500">
                      <Star className="w-4 h-4 fill-current" />
                      <span className="font-medium">{pkg.rating ?? 4.5}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
export const TestimonialsSection = ({ section, testimonials = [] }) => {
  const showCount = section.showCount || 4;
  const displayTestimonials = testimonials.slice(0, showCount);
  
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          {section.badge && (
            <span className="inline-block px-3 py-1 bg-rose-100 text-rose-600 rounded-full text-sm font-medium mb-2">
              {section.badge}
            </span>
          )}
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800">{section.title}</h2>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {displayTestimonials.map((testimonial) => (
            <Card key={testimonial.id} className="border-0 shadow-lg rounded-3xl p-6">
              <Quote className="w-8 h-8 text-rose-300 mb-4" />
              <p className="text-slate-600 mb-4 line-clamp-4">{testimonial.content || testimonial.text}</p>
              <div className="flex items-center gap-3">
                <img 
                  src={optimized(testimonial.avatar || testimonial.image || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100')} 
                  alt={testimonial.name} 
                  loading="lazy"
                  decoding="async"
                  className="w-10 h-10 rounded-full object-cover" 
                />
                <div>
                  <p className="font-semibold text-slate-800">{testimonial.name}</p>
                  <p className="text-sm text-slate-500">{testimonial.destination || testimonial.location}</p>
                </div>
              </div>
              <div className="flex gap-1 mt-3">
                {[...Array(testimonial.rating || 5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-amber-500 fill-current" />
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
export const CTASection = ({ section }) => {
  let bgClass = 'bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500';
  let bgStyle = {};
  
  if (section.backgroundColor === 'solid' && section.solidColor) {
    bgClass = '';
    bgStyle = { backgroundColor: section.solidColor };
  } else if (section.backgroundColor === 'image' && section.backgroundImage) {
    bgClass = 'bg-cover bg-center';
    bgStyle = { backgroundImage: `url(${section.backgroundImage})` };
  }
  
  return (
    <section className={`relative py-16 ${bgClass}`} style={bgStyle}>
      {section.backgroundColor === 'image' && section.backgroundImage && (
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 to-slate-900/70" />
      )}
      <div className="relative container mx-auto px-4 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{section.title}</h2>
        {section.subtitle && <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">{section.subtitle}</p>}
        {section.buttonText && (
          <a href={section.buttonLink || '/inquiry'} target="_blank" rel="noopener noreferrer">
            <Button className="bg-white text-rose-500 hover:bg-slate-100 rounded-full px-8 py-6 text-lg font-semibold">
              {section.buttonText} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        )}
      </div>
    </section>
  );
};

// Partners Logos Section
export const PartnersLogosSection = ({ section }) => {
  const siteSettings = useSiteSettings();
  
  // Use footer partners (global source) if available, fall back to section data
  const footerPartners = siteSettings?.footer?.partners;
  const logos = footerPartners?.logos?.length ? footerPartners.logos : (section.logos || []);
  const title = footerPartners?.title || section.title;
  
  if (logos.length === 0) return null;
  
  // Duplicate logos for seamless marquee loop
  const marqueeLogos = [...logos, ...logos, ...logos];
  
  return (
    <section className="py-16 bg-slate-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {title && (
          <h2 className="text-4xl font-bold text-slate-800 text-center mb-12">{title}</h2>
        )}
        <div className="relative">
          <div className="flex gap-16 items-center animate-marquee">
            {marqueeLogos.map((logo, idx) => {
              const logoSrc = normalizeImageUrl(logo.image || logo.logo);
              return (
                <div key={idx} className="flex-shrink-0 transition-transform duration-300 hover:scale-125">
                  {logoSrc ? (
                    logo.website ? (
                      <a href={logo.website} target="_blank" rel="noopener noreferrer">
                        <img src={logoSrc} alt={logo.alt_text || logo.name || 'Partner logo'} className="h-24 w-auto object-contain" onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                      </a>
                    ) : (
                      <img src={logoSrc} alt={logo.alt_text || logo.name || 'Partner logo'} className="h-24 w-auto object-contain" onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                    )
                  ) : (
                    <span className="text-slate-400 font-semibold text-lg whitespace-nowrap">{logo.name}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        .animate-marquee {
          animation: marquee 40s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
};

// Text Block Section
export const TextSection = ({ section }) => {
  const alignment = section.alignment || 'center';
  const alignClass = alignment === 'left' ? 'text-left' : alignment === 'right' ? 'text-right' : 'text-center';
  
  return (
    <section className="py-16 bg-white">
      <div className={`container mx-auto px-4 max-w-4xl ${alignClass}`}>
        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">{section.title}</h2>
        {section.content && <p className="text-slate-600 text-lg leading-relaxed">{section.content}</p>}
      </div>
    </section>
  );
};

// Content Block Section (WYSIWYG HTML)
export const ContentBlockSection = ({ section }) => {
  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 lg:px-8">
        {section.title && (
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">{section.title}</h2>
        )}
        <div
          className="prose prose-slate max-w-none prose-headings:text-slate-800 prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3 prose-p:text-slate-600 prose-p:leading-relaxed prose-a:text-rose-500 prose-a:no-underline hover:prose-a:underline prose-li:text-slate-600 prose-strong:text-slate-700 prose-ul:my-4 prose-li:my-1"
          dangerouslySetInnerHTML={{ __html: section.content || '' }}
        />
      </div>
    </section>
  );
};

// Stats Grid Section (different from Stats Bar)
export const StatsGridSection = ({ section }) => {
  const stats = section.data || [];
  
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 text-center mb-10">{section.title}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, idx) => (
            <Card key={idx} className="border-0 shadow-lg rounded-2xl p-6 text-center">
              <p className="text-4xl font-bold text-rose-500 mb-2">{stat.value}</p>
              <p className="text-slate-600">{stat.label}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Mission Section
export const MissionSection = ({ section }) => {
  return (
    <section className="py-16 bg-gradient-to-r from-rose-50 to-orange-50">
      <div className="container mx-auto px-4 max-w-4xl text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">{section.title}</h2>
        {section.content && <p className="text-slate-600 text-lg leading-relaxed">{section.content}</p>}
      </div>
    </section>
  );
};

// Gallery Section
export const GallerySection = ({ section }) => {
  const images = section.images || [];
  const columns = section.columns || 3;
  
  const gridClass = columns === 2 ? 'md:grid-cols-2' : 
                    columns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 
                    'md:grid-cols-2 lg:grid-cols-3';

  if (images.length === 0) return null;

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {section.title && (
          <h2 className="text-4xl font-bold text-slate-800 text-center mb-12">{section.title}</h2>
        )}
        <div className={`grid ${gridClass} gap-4`}>
          {images.map((image, idx) => (
            <div key={idx} className="group relative overflow-hidden rounded-2xl aspect-square">
              <img 
                src={optimized(image.url)} 
                alt={image.alt || image.caption} 
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              {image.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/90 to-transparent p-4">
                  <p className="text-white text-sm font-medium">{image.caption}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Mission Section Renderer
const MissionSectionRenderer = ({ section }) => {
  const stats = section.overlayStats || section.stats || [];
  
  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            {section.badge && (
              <span className="inline-block bg-rose-100 text-rose-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">
                {section.badge}
              </span>
            )}
            <h2 className="text-4xl font-bold text-slate-800 mb-6">{section.title}</h2>
            <div className="text-slate-600 leading-relaxed whitespace-pre-line mb-8">{section.content}</div>
            {stats.length > 0 && (
              <div className="grid grid-cols-2 gap-6">
                {stats.map((stat, idx) => (
                  <div key={idx} className="flex items-start p-4 bg-white rounded-2xl shadow-sm">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${idx % 2 === 0 ? 'bg-rose-100' : 'bg-teal-100'}`}>
                      <Globe className={`w-6 h-6 ${idx % 2 === 0 ? 'text-rose-500' : 'text-teal-500'}`} />
                    </div>
                    <div className="ml-3">
                      <h4 className="font-bold text-slate-800">{stat.value}</h4>
                      <p className="text-sm text-slate-500">{stat.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {section.image && (
            <div className="relative">
              <img src={optimized(section.image)} alt={section.title} loading="lazy" decoding="async" className="w-full h-[500px] object-cover rounded-3xl shadow-xl" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Promises Grid Renderer
const PromisesGridRenderer = ({ section }) => {
  const promises = section.promises || [];
  const iconMap = { Shield, Heart, Clock, Award, Globe, Target, Users, Star };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          {section.badge && (
            <span className="inline-block bg-amber-100 text-amber-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">{section.badge}</span>
          )}
          <h2 className="text-4xl font-bold text-slate-800 mb-4">{section.title}</h2>
          {section.subtitle && <p className="text-slate-600 max-w-2xl mx-auto">{section.subtitle}</p>}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {promises.map((promise, index) => {
            const Icon = iconMap[promise.icon] || Star;
            return (
              <Card key={index} className="border-0 shadow-lg rounded-3xl overflow-hidden group hover:-translate-y-2 transition-transform">
                <div className={`h-2 ${index % 4 === 0 ? 'bg-rose-500' : index % 4 === 1 ? 'bg-orange-500' : index % 4 === 2 ? 'bg-amber-500' : 'bg-teal-500'}`} />
                <CardContent className="p-6 text-center">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 ${index % 4 === 0 ? 'bg-rose-100' : index % 4 === 1 ? 'bg-orange-100' : index % 4 === 2 ? 'bg-amber-100' : 'bg-teal-100'}`}>
                    <Icon className={`w-7 h-7 ${index % 4 === 0 ? 'text-rose-500' : index % 4 === 1 ? 'text-orange-500' : index % 4 === 2 ? 'text-amber-500' : 'text-teal-500'}`} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">{promise.title}</h3>
                  <p className="text-slate-600 text-sm">{promise.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Values Grid Renderer
const ValuesGridRenderer = ({ section }) => {
  const values = section.values || [];
  const iconMap = { Heart, Globe, Users, Star, Target, Lightbulb, CheckCircle, Shield };

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          {section.badge && (
            <span className="inline-block bg-orange-100 text-orange-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">{section.badge}</span>
          )}
          <h2 className="text-4xl font-bold text-slate-800">{section.title}</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => {
            const Icon = iconMap[value.icon] || Star;
            return (
              <Card key={index} className="border-0 shadow-lg rounded-3xl p-6 text-center hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-gradient-to-br from-rose-100 to-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-8 h-8 text-rose-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">{value.title}</h3>
                <p className="text-slate-600 text-sm">{value.description}</p>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Timeline Renderer
const TimelineRenderer = ({ section }) => {
  const milestones = section.milestones || [];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-slate-800 mb-4">{section.title}</h2>
        </div>
        <div className="relative">
          <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-rose-500 via-orange-500 to-amber-500 -translate-x-1/2 hidden lg:block" />
          <div className="space-y-12">
            {milestones.map((milestone, index) => (
              <div key={index} className={`flex flex-col lg:flex-row items-center gap-8 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                <div className="flex-1">
                  <Card className="border-0 shadow-lg rounded-3xl p-6 hover:shadow-xl transition-shadow">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-orange-500 rounded-2xl flex items-center justify-center text-white font-bold">{milestone.year}</div>
                      <h3 className="text-xl font-bold text-slate-800">{milestone.title}</h3>
                    </div>
                    <p className="text-slate-600">{milestone.description}</p>
                  </Card>
                </div>
                <div className="w-4 h-4 bg-gradient-to-br from-rose-500 to-orange-500 rounded-full flex-shrink-0 hidden lg:block ring-4 ring-white" />
                <div className="flex-1 hidden lg:block" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Team Grid Renderer
const TeamGridRenderer = ({ section }) => {
  const members = section.members || [];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          {section.badge && (
            <span className="inline-block bg-rose-100 text-rose-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">{section.badge}</span>
          )}
          <h2 className="text-4xl font-bold text-slate-800">{section.title}</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {members.map((member, index) => (
            <Card key={index} className="border-0 shadow-lg rounded-3xl overflow-hidden group hover:-translate-y-2 transition-transform">
              <div className="relative h-64 overflow-hidden">
                {member.image ? (
                  <img src={optimized(member.image)} alt={member.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-rose-100 to-orange-100 flex items-center justify-center">
                    <Users className="w-16 h-16 text-rose-400" />
                  </div>
                )}
              </div>
              <CardContent className="p-6 text-center">
                <h3 className="text-lg font-bold text-slate-800 mb-1">{member.name}</h3>
                <p className="text-sm text-rose-500 font-medium mb-2">{member.role}</p>
                {member.bio && <p className="text-slate-600 text-sm line-clamp-2">{member.bio}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Cards Renderer
const ContactCardsRenderer = ({ section }) => {
  const cards = section.cards || [];
  const iconMap = { MapPin, Phone, Mail, Clock };

  return (
    <section className="py-12 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid md:grid-cols-4 gap-6">
          {cards.map((card, index) => {
            const Icon = iconMap[card.icon] || MapPin;
            const colorClasses = [
              { border: 'from-rose-500 to-pink-500', bg: 'bg-rose-100', icon: 'text-rose-500' },
              { border: 'from-orange-500 to-amber-500', bg: 'bg-orange-100', icon: 'text-orange-500' },
              { border: 'from-teal-500 to-cyan-500', bg: 'bg-teal-100', icon: 'text-teal-500' },
              { border: 'from-purple-500 to-pink-500', bg: 'bg-purple-100', icon: 'text-purple-500' }
            ];
            const colors = colorClasses[index % 4];

            return (
              <Card key={index} className="border-0 shadow-lg rounded-3xl overflow-hidden group hover:-translate-y-2 transition-transform">
                <div className={`h-1 bg-gradient-to-r ${colors.border}`} />
                <CardContent className="p-6 text-center">
                  <div className={`w-14 h-14 ${colors.bg} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                    <Icon className={`w-7 h-7 ${colors.icon}`} />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2">{card.title}</h3>
                  <p className="text-sm text-slate-600 whitespace-pre-line">{card.content}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Contact Form Renderer
const ContactFormRenderer = ({ section }) => {
  return (
    <section className="py-16 bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 lg:px-8">
        <Card className="border-0 shadow-xl rounded-3xl overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-rose-500 via-orange-500 to-amber-500" />
          <CardContent className="p-8 lg:p-12">
            <h2 className="text-2xl font-bold text-slate-800 mb-8 text-center">{section.title}</h2>
            <p className="text-center text-slate-500">Contact form is functional on the website</p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

// Map Placeholder Renderer
const MapPlaceholderRenderer = ({ section }) => {
  return (
    <section className={`h-${section.height || '96'} bg-slate-200 relative`}>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-slate-300 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-8 h-8 text-slate-400" />
          </div>
          <p className="text-slate-500 font-medium">{section.message}</p>
        </div>
      </div>
    </section>
  );
};

// Video Embed Section
export const VideoEmbedSection = ({ section }) => {
  const videos = section.videos || [];

  const getVideoEmbedUrl = (video) => {
    if (!video.url) return null;
    
    if (video.provider === 'youtube') {
      const videoId = video.url.includes('youtube.com') 
        ? new URL(video.url).searchParams.get('v')
        : video.url.replace('https://youtu.be/', '').split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (video.provider === 'vimeo') {
      const videoId = video.url.split('/').pop().split('?')[0];
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return null;
  };

  if (videos.length === 0) return null;

  return (
    <section className="py-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {section.title && (
          <h2 className="text-4xl font-bold text-slate-800 text-center mb-12">{section.title}</h2>
        )}
        <div className="grid md:grid-cols-2 gap-6">
          {videos.map((video, idx) => {
            const embedUrl = getVideoEmbedUrl(video);
            return (
              <div key={idx} className="bg-white rounded-2xl overflow-hidden shadow-lg">
                {embedUrl ? (
                  <div className="aspect-video">
                    <iframe
                      src={embedUrl}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-slate-200 flex items-center justify-center">
                    <p className="text-slate-400">Invalid video URL</p>
                  </div>
                )}
                {video.title && (
                  <div className="p-4">
                    <p className="font-semibold text-slate-800">{video.title}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Benefits Section Renderer
const BenefitsSectionRenderer = ({ section }) => {
  const benefits = section.benefits || [];
  const images = section.images || [];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          {section.badge && (
            <span className="inline-block bg-teal-100 text-teal-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">{section.badge}</span>
          )}
          <h2 className="text-4xl font-bold text-slate-800">{section.title}</h2>
        </div>
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Benefits List */}
          <div className="space-y-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg rounded-3xl p-6 hover:shadow-xl transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">{benefit.title}</h3>
                    <p className="text-slate-600">{benefit.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          {/* Side Images */}
          {images.length > 0 && (
            <div className="grid grid-cols-2 gap-4">
              {images.map((img, index) => (
                <div key={index} className={`relative overflow-hidden rounded-3xl ${index === 0 ? 'col-span-2 h-64' : 'h-48'}`}>
                  <img src={optimized(img)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

// Main Section Renderer Component

// Featured Destinations Section
const FeaturedDestinationsSection = ({ section }) => {
  const [destinations, setDestinations] = React.useState([]);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${getApiBase()}/api/destinations/featured?limit=${section.showCount || 4}`);
        const data = await response.json();
        setDestinations(data);
      } catch (error) {
        console.error('Error fetching featured destinations:', error);
      }
    };
    fetchData();
  }, [section.showCount]);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex justify-between items-end mb-12">
          <div>
            {section.badge && (
              <span className="inline-block bg-amber-100 text-amber-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">
                {section.badge}
              </span>
            )}
            <h2 className="text-4xl font-bold text-slate-800">{section.title || 'Featured Destinations'}</h2>
          </div>
          <a href="/destinations" target="_blank" rel="noopener noreferrer" className="text-rose-500 hover:text-rose-600 font-semibold flex items-center group">
            {section.linkText || 'View All'}
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {destinations.map((destination) => (
            <a key={destination.id} href={`/destinations/${destination.slug || destination.id}`} target="_blank" rel="noopener noreferrer">
              <Card className="group relative overflow-hidden rounded-3xl border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="relative h-80">
                  <img src={optimized(destination.image)} alt={destination.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex gap-2 mb-3">
                      {(destination.themes || []).slice(0, 2).map((theme) => (
                        <span key={theme} className="text-xs bg-white/20 backdrop-blur-sm text-white px-2 py-1 rounded-full capitalize">{theme}</span>
                      ))}
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-1">{destination.name}</h3>
                    <div className="flex items-center justify-between">
                      <span className="text-white/80 text-sm">From ₹{(destination.min_budget || destination.minBudget || 0).toLocaleString('en-IN')}</span>
                      <span className="text-rose-400 font-semibold text-sm group-hover:translate-x-2 transition-transform flex items-center">
                        Explore <ArrowRight className="w-4 h-4 ml-1" />
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// Trending Now Section (Mixed Destinations + Packages)
const TrendingNowSection = ({ section }) => {
  const [items, setItems] = React.useState([]);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${getApiBase()}/api/cms/trending-now?limit=${section.showCount || 8}`);
        const data = await response.json();
        setItems(data);
      } catch (error) {
        console.error('Error fetching trending items:', error);
      }
    };
    fetchData();
  }, [section.showCount]);

  if (items.length === 0) return null;

  return (
    <section className="py-20 bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          {section.badge && (
            <span className="inline-block bg-rose-100 text-rose-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">
              {section.badge}
            </span>
          )}
          <h2 className="text-4xl font-bold text-slate-800">{section.title || 'Trending Now'}</h2>
          {section.subtitle && <p className="text-slate-600 mt-2">{section.subtitle}</p>}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((item) => (
            <a 
              key={item.id} 
              href={item.item_type === 'destination' 
                ? `/destinations/${item.slug || item.id}` 
                : getPackageUrl(item)
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              <Card className="group relative overflow-hidden rounded-3xl border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="relative h-80">
                  <img src={optimized(item.image)} alt={item.name || item.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-rose-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> TRENDING
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex gap-2 mb-3">
                      {item.item_type === 'destination' ? (
                        <span className="text-xs bg-amber-500 text-white px-2 py-1 rounded-full font-semibold">📍 Destination</span>
                      ) : (
                        <>
                          <span className="text-xs bg-teal-500 text-white px-2 py-1 rounded-full font-semibold">📦 Package</span>
                          {item.duration && <span className="text-xs bg-white/20 backdrop-blur-sm text-white px-2 py-1 rounded-full">{item.duration} Days</span>}
                        </>
                      )}
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-1">{item.name || item.title}</h3>
                    <div className="flex items-center justify-between">
                      <span className="text-white/80 text-sm">From ₹{(item.min_budget || item.price || 0).toLocaleString('en-IN')}</span>
                      <span className="text-rose-400 font-semibold text-sm group-hover:translate-x-2 transition-transform flex items-center">
                        Explore <ArrowRight className="w-4 h-4 ml-1" />
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// Spotlight Blogs Section
const SpotlightBlogsSection = ({ section }) => {
  const [blogs, setBlogs] = React.useState([]);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${getApiBase()}/api/cms/spotlight-blogs?limit=${section.showCount || 3}`);
        const data = await response.json();
        setBlogs(data);
      } catch (error) {
        console.error('Error fetching spotlight blogs:', error);
      }
    };
    fetchData();
  }, [section.showCount]);

  if (blogs.length === 0) return null;

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          {section.badge && (
            <span className="inline-block bg-purple-100 text-purple-600 px-4 py-1 rounded-full text-sm font-semibold mb-4">
              {section.badge}
            </span>
          )}
          <h2 className="text-4xl font-bold text-slate-800">{section.title || 'Travel Inspiration'}</h2>
          {section.subtitle && <p className="text-slate-600 mt-2">{section.subtitle}</p>}
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {blogs.map((blog) => (
            <a key={blog.id} href={`/blog/${blog.slug || blog.id}`} target="_blank" rel="noopener noreferrer">
              <Card className="group overflow-hidden rounded-3xl border-0 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                <div className="relative h-56 overflow-hidden">
                  <img src={optimized(blog.image)} alt={blog.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }} />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-purple-500 text-white text-xs font-bold rounded-full">SPOTLIGHT</span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3 text-sm text-slate-500">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(blog.created_at || blog.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    <span className="mx-2">•</span>
                    <span>{blog.read_time || blog.readTime || 5} min read</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2 line-clamp-2 group-hover:text-rose-600 transition-colors">{blog.title}</h3>
                  <p className="text-slate-600 text-sm line-clamp-3">{blog.excerpt || blog.content?.substring(0, 120) + '...'}</p>
                  <div className="mt-4 text-rose-500 font-semibold text-sm flex items-center group-hover:translate-x-2 transition-transform">
                    Read More <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </CardContent>
              </Card>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

const SectionRenderer = ({ section, data = {} }) => {
  const { destinations = [], packages = [], testimonials = [], themes = [] } = data;
  
  switch (section.type) {
    case 'statsBar':
      return <StatsBarSection section={section} />;
    case 'destinationsGrid':
      return <DestinationsGridSection section={section} destinations={destinations} />;
    case 'featuredDestinations':
      return <FeaturedDestinationsSection section={section} />;
    case 'trendingNow':
      return <TrendingNowSection section={section} />;
    case 'spotlightBlogs':
      return <SpotlightBlogsSection section={section} />;
    case 'themesGrid':
      return <ThemesGridSection section={section} themes={themes} />;
    case 'packagesGrid':
      return <PackagesGridSection section={section} packages={packages} />;
    case 'testimonials':
      return <TestimonialsSection section={section} testimonials={testimonials} />;
    case 'cta':
      return <CTASection section={section} />;
    case 'partnersLogos':
      return <PartnersLogosSection section={section} />;
    case 'text':
      return <TextSection section={section} />;
    case 'contentBlock':
      return <ContentBlockSection section={section} />;
    case 'statsGrid':
      return <StatsGridSection section={section} />;
    case 'missionSection':
      return <MissionSectionRenderer section={section} />;
    case 'gallery':
      return <GallerySection section={section} />;
    case 'videoEmbed':
      return <VideoEmbedSection section={section} />;
    case 'promisesGrid':
      return <PromisesGridRenderer section={section} />;
    case 'valuesGrid':
      return <ValuesGridRenderer section={section} />;
    case 'timeline':
      return <TimelineRenderer section={section} />;
    case 'teamGrid':
      return <TeamGridRenderer section={section} />;
    case 'benefitsSection':
      return <BenefitsSectionRenderer section={section} />;
    case 'contactCards':
      return <ContactCardsRenderer section={section} />;
    case 'contactForm':
      return <ContactFormRenderer section={section} />;
    case 'mapPlaceholder':
      return <MapPlaceholderRenderer section={section} />;
    default:
      return null;
  }
};

export default SectionRenderer;
